/**
 * ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════════
 * TK-33b (lee 22/10 chiều) — BẢNG TỦ ĐANG CHỌN dưới bản đồ trang chính (mẫu chạy được `.inspector`). Bản RÚT GỌN của
 *   trang tủ (`/tu/:id` giữ nguyên) — dữ liệu, quyền và lệnh là của Kapp:
 *     · đầu: "Tủ <tên>" · chip Trực tuyến / Mất kết nối (XÁM) · chip Tự động / Tay (ngõ ra có nguồn giữ) · mã · tuyến ·
 *       "Bản tin gần nhất · HH:MM:SS" · "Mở trang tủ" · đóng.
 *     · tab Tổng quan (3 pha U/I/P/cos φ · công suất 24 giờ · trạng thái ngõ ra + "Trả về tự động") · Ngõ ra (`OutputCard`
 *       của trang tủ) · Đèn NEMA (cờ bật) · Lịch vận hành · Lịch sử (khung Vấn đề phạm vi tủ + lệnh gần đây).
 * ⛔ LUẬT SỐ 0: tủ mất kết nối ⇒ bảng 3 pha "—", công tắc khoá; không có số đo ⇒ "Chưa có số đo", không vẽ đường 0.
 * ⛔ KHOÁ-KHÔNG-ẨN: công tắc + "Trả về tự động" đi qua `<KhoaTheoTuyen co="canControl">` — thiếu cờ thì hiện mà khoá.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════════════════════════
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RotateCcw, Server, X } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { KIEU_GOI_Y, defsMien, toMien, useIdMien } from './bieudo/Mien';
import { api, errorMessage, ApiError } from '../lib/api';
import {
  qk, useCabinet, useCommands, useMeterHistory, useMeterLatest, useNguonGiuSong, useSoDoTrucTiep,
} from '../lib/queries';
import { useAuth } from '../lib/auth';
import { useNemaBat } from '../lib/nema';
import { KhoaTheoTuyen } from './Khoa';
import { ErrorBox, LOP_CHUA_RO, Spinner, Switch, useToast } from './ui';
import { HopVanDe } from './HopVanDe';
import { TabDenNema } from './nema/TabDenNema';
import { BangLenh, OutputCard } from '../pages/CabinetDetail';
import { diemTheoDonVi, duLieuBieuDo } from '../lib/bieudo';
import { hienCongSuat } from '../lib/congsuat';
import { nhanGiuNgo } from '../lib/nguongiu';
import { MODE_LABEL, type CommandState } from '../lib/types';
import { fmtNum, minToStr } from '../lib/format';

type TabBang = 'tong-quan' | 'ngo-ra' | 'den' | 'lich' | 'lich-su';
const hai = (n: number) => String(n).padStart(2, '0');
function gioGiay(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : `${hai(d.getHours())}:${hai(d.getMinutes())}:${hai(d.getSeconds())}`;
}

export function BangTu({ cabinetId, onDong }: { cabinetId: string; onDong: () => void }) {
  const cab = useCabinet(cabinetId);
  const sdSong = useSoDoTrucTiep(cabinetId);
  const sdMoi = useMeterLatest(cabinetId);
  const meter = useMeterHistory(cabinetId, 24);
  const idMien = useIdMien();   // KA-49 §3: gradient riêng của biểu đồ này
  const nguonGiuSong = useNguonGiuSong(cabinetId);
  const commands = useCommands(cabinetId, 10);
  const { coCoTuyen } = useAuth();
  const coNema = useNemaBat().trangThai === 'bat';
  const [tab, setTab] = useState<TabBang>('tong-quan');
  const qc = useQueryClient();
  const toast = useToast();

  const lamMoi = () => {
    void qc.invalidateQueries({ queryKey: qk.cabinet(cabinetId) });
    void qc.invalidateQueries({ queryKey: qk.commands(cabinetId) });
    void qc.invalidateQueries({ queryKey: qk.tree });
  };
  /* Cùng hai cửa lệnh của trang tủ (`/control`, `/release`) — máy chủ gác quyền lần nữa. */
  const control = useMutation({
    mutationFn: (v: { index: number; on: boolean }) =>
      api.post<{ commandId: string; state: CommandState; ackMode: string }>(`/api/cabinets/${cabinetId}/control`, { outputs: [{ index: v.index, on: v.on }] }),
    onSuccess: (res, v) => {
      toast.success(`Đã gửi lệnh ${v.on ? 'BẬT' : 'TẮT'} ngõ ra ${v.index}`,
        res.ackMode === 'INFERRED' ? 'Tủ chạy giao thức v1 nên không có xác nhận tường minh — trạng thái sẽ cập nhật khi tủ báo về.' : 'Đang chờ tủ xác nhận.');
      lamMoi();
    },
    onError: (e) => toast.error(errorMessage(e), e instanceof ApiError ? (e.hint ?? undefined) : undefined),
  });
  const release = useMutation({
    mutationFn: () => api.post<{ commandId: string; state: CommandState }>(`/api/cabinets/${cabinetId}/release`),
    onSuccess: () => { toast.success('Đã gửi lệnh trả quyền về chế độ tự động', 'Xong khi ba ngõ ra hết nhãn “Đang giữ” ở lần tủ báo về kế tiếp.'); lamMoi(); },
    onError: (e) => toast.error('Không trả quyền được', errorMessage(e)),
  });

  const donViCongTo = cab.data?.donViCongSuat ?? null;
  const { diem, donViP } = useMemo(
    () => diemTheoDonVi(meter.data ? duLieuBieuDo(meter.data) : [], donViCongTo), [meter.data, donViCongTo]);
  const coDiem = diem.some((d) => d.p != null);

  if (cab.isLoading) return <section className="border-t border-vien-manh bg-nen-0 p-4" data-o="bang-tu"><Spinner label="Đang tải tủ…" /></section>;
  if (cab.isError || !cab.data) return <section className="border-t border-vien-manh bg-nen-0 p-4" data-o="bang-tu"><ErrorBox error={cab.error} onRetry={() => void cab.refetch()} /></section>;

  const c = cab.data;
  const online = c.online;
  const qd = coCoTuyen('canControl', c.route?.id ?? null);
  const coQuyenBat = qd === 'cho-qua';
  /* Chế độ ngõ ra: có ngõ đang bị một nguồn GIỮ ⇒ "Tay", không ⇒ "Tự động". Mất kết nối ⇒ không khai (số đông lạnh). */
  const tay = c.outputs.some((o) => nhanGiuNgo(nguonGiuSong(o.index) ?? o.nguonGiuQuyen, o.manualHold).kieu !== 'trong');
  const banTin = gioGiay(c.lastSeenAt);

  /* 3 pha: khung sống thắng hàng CSDL (tươi hơn); mất kết nối ⇒ "—" như mẫu. */
  const m: Record<string, number | null | undefined> | null = sdSong.data?.meter ?? (sdMoi.data as unknown as Record<string, number | null> | null) ?? null;
  const soPha = c.soPha ?? 3;
  const o = (v: number | null | undefined, chuSo: number) => (online && v != null && Number.isFinite(v) ? fmtNum(v, chuSo) : '—');
  const donViHang = donViCongTo ?? '';
  const tongP = online && m && m.power != null && Number.isFinite(m.power) ? hienCongSuat(m.power as number, donViCongTo) : null;

  const TABS: { k: TabBang; ten: string }[] = [
    { k: 'tong-quan', ten: 'Tổng quan' }, { k: 'ngo-ra', ten: 'Ngõ ra' },
    ...(coNema ? [{ k: 'den' as const, ten: 'Đèn' }] : []),
    { k: 'lich', ten: 'Lịch vận hành' }, { k: 'lich-su', ten: 'Lịch sử' },
  ];

  return (
    <section className="border-t border-vien-manh bg-nen-0 p-3 md:p-4" data-o="bang-tu" data-tu={c.id}>
      <div className="flex flex-wrap items-center gap-3">
        <Server className={`h-[17px] w-[17px] ${online ? 'text-tot' : 'text-chu-3'}`} aria-hidden="true" />
        <h2 className="m-0 text-base font-semibold text-chu-1" data-o="ten-bang-tu">{/^tủ\s/i.test(c.name) ? c.name : `Tủ ${c.name}`}</h2>
        <span className={`rounded-nut px-2 py-1 text-[11px] ${online ? 'bg-tot/15 text-tot' : 'bg-nen-2 text-chu-3'}`} data-o="chip-ket-noi">{online ? 'Trực tuyến' : 'Mất kết nối'}</span>
        {online && <span className="rounded-nut bg-tot/15 px-2 py-1 text-[11px] text-tot" data-o="chip-che-do">{tay ? 'Tay' : 'Tự động'}</span>}
        <span className="text-[11px] text-chu-2">{[c.code, c.route?.tenDayDu].filter(Boolean).join(' · ')}</span>
        <span className="ml-auto flex items-center gap-3 text-[11px] text-chu-2">
          <span>Bản tin gần nhất · {banTin ? <span className="tabular-nums">{banTin}</span> : <span className={LOP_CHUA_RO}>chưa có</span>}</span>
          <Link to={`/tu/${c.id}`} className="text-nhan hover:underline" data-o="mo-trang-tu">Mở trang tủ</Link>
          <button type="button" onClick={onDong} aria-label="Đóng bảng tủ" className="rounded-nut p-1 text-chu-2 hover:bg-nen-2 hover:text-chu-1"><X className="h-4 w-4" /></button>
        </span>
      </div>

      <div role="tablist" aria-label="Bảng tủ" className="mb-3 mt-3 flex gap-[22px] overflow-x-auto border-b border-vien">
        {TABS.map((t) => (
          <button key={t.k} type="button" role="tab" aria-selected={tab === t.k} data-tab-bang={t.k} onClick={() => setTab(t.k)}
            className={`-mb-px shrink-0 whitespace-nowrap border-0 border-b-2 bg-transparent px-0 py-2 text-xs ${tab === t.k ? 'border-nhan text-nhan' : 'border-transparent text-chu-2 hover:text-chu-1'}`}>
            {t.ten}
          </button>
        ))}
      </div>

      {tab === 'tong-quan' && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-[1.1fr_1.25fr_.9fr]">
          <section className="min-w-0" data-o="bang-3-pha">
            <h3 className="m-0 mb-2.5 text-xs font-semibold text-chu-1">Điện áp / Dòng điện / Công suất</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[11px] tabular-nums">
                <thead>
                  <tr className="bg-nen-2 text-chu-2">
                    <th className="px-1.5 py-[7px] text-left font-normal">Pha</th><th className="px-1.5 py-[7px] text-right font-normal">U (V)</th>
                    <th className="px-1.5 py-[7px] text-right font-normal">I (A)</th><th className="px-1.5 py-[7px] text-right font-normal">P{donViHang ? ` (${donViHang})` : ''}</th>
                    <th className="px-1.5 py-[7px] text-right font-normal">cos φ</th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3].filter((k) => k <= soPha).map((k) => (
                    <tr key={k} className="border-b border-vien text-chu-1">
                      <td className="px-1.5 py-[7px]">{soPha === 1 ? '—' : ['A', 'B', 'C'][k - 1]}</td>
                      <td className="px-1.5 py-[7px] text-right">{o(m?.[`v${k}`], 1)}</td>
                      <td className="px-1.5 py-[7px] text-right">{o(m?.[`i${k}`], 2)}</td>
                      <td className="px-1.5 py-[7px] text-right">{o(m?.[`p${k}`], 1)}</td>
                      <td className="px-1.5 py-[7px] text-right">{o(m?.[`a${k}`], 2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="m-0 mt-2 text-[11px] text-chu-2" data-o="tong-3-pha">
              {!online ? 'Không có dữ liệu trực tuyến'
                : !m ? <span className={LOP_CHUA_RO}>Chưa có số đo</span>
                  : tongP ? <>Tổng: <span className="tabular-nums text-chu-1">{fmtNum(tongP.tri, tongP.chuSo)}{tongP.donVi ? ` ${tongP.donVi}` : ''}</span></> : null}
            </p>
          </section>

          <section className="min-w-0 xl:border-l xl:border-vien xl:pl-[15px]" data-o="do-thi-24h">
            <h3 className="m-0 mb-2.5 text-xs font-semibold text-chu-1">Công suất tủ · 24 giờ {donViP && <span className="font-normal text-chu-2">({donViP})</span>}</h3>
            {meter.isLoading ? <Spinner label="Đang tải…" />
              : !coDiem ? <p className={`m-0 py-10 text-center text-xs ${LOP_CHUA_RO}`} data-o="chua-co-so-do">Chưa có số đo</p>
                : (
                  <div className="h-[124px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={diem} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                        {defsMien(idMien, 1)}
                        <CartesianGrid stroke="rgb(var(--vien))" vertical={false} />
                        <XAxis dataKey="ms" type="number" scale="time" domain={['dataMin', 'dataMax']} tick={{ fill: 'rgb(var(--chu-2))', fontSize: 11 }} tickCount={5} interval="preserveStartEnd" minTickGap={60}
                          tickFormatter={(v: number) => { const d = new Date(v); return `${hai(d.getHours())}:${hai(d.getMinutes())}`; }} />
                        <YAxis tick={{ fill: 'rgb(var(--chu-2))', fontSize: 11 }} width={48} tickFormatter={(v: number) => v.toLocaleString('vi-VN', { maximumFractionDigits: 1 })} />
                        <Tooltip {...KIEU_GOI_Y}
                          labelFormatter={(v: number) => new Date(v).toLocaleString('vi-VN')} />
                        {/* `connectNulls={false}`: ô thiếu số là KHOẢNG TRỐNG, không phải đường nối qua (luật số 0). */}
                        <Area dataKey="p" type="monotone" stroke="rgb(var(--nhan-sang))" strokeWidth={1.8} fill={toMien(idMien, 0)} fillOpacity={1} connectNulls={false} isAnimationActive={false} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
          </section>

          <section className="min-w-0 md:col-span-2 xl:col-span-1 xl:border-l xl:border-vien xl:pl-[15px]" data-o="trang-thai-ngo-ra">
            <h3 className="m-0 mb-2.5 text-xs font-semibold text-chu-1">Trạng thái ngõ ra</h3>
            <KhoaTheoTuyen qd={qd} co="canControl">
              {c.outputs.map((ng) => (
                <div key={ng.id} className="flex items-center justify-between gap-2 border-b border-vien py-[7px] text-xs" data-ngo={ng.index}>
                  <span className="min-w-0 truncate text-chu-1">{ng.name || `Ngõ ra ${ng.index}`}</span>
                  <Switch checked={ng.isOn} disabled={!coQuyenBat || !online} busy={control.isPending && control.variables?.index === ng.index}
                    onChange={(v) => control.mutate({ index: ng.index, on: v })} labelOn="Đang bật" labelOff="Đã tắt" />
                </div>
              ))}
              <button type="button" disabled={!coQuyenBat || !online || release.isPending} onClick={() => release.mutate()} data-o="tra-ve-tu-dong"
                className="mt-2.5 inline-flex w-full items-center justify-center gap-2 rounded-nut border border-vien px-3 py-2 text-xs text-chu-1 hover:bg-nen-2 disabled:cursor-not-allowed disabled:opacity-50">
                <RotateCcw className="h-[15px] w-[15px]" aria-hidden="true" />Trả về tự động
              </button>
            </KhoaTheoTuyen>
          </section>
        </div>
      )}

      {tab === 'ngo-ra' && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {c.outputs.map((ng) => (
            <OutputCard key={ng.id} output={ng} nguonGiuSong={nguonGiuSong(ng.index)} online={online} qd={qd}
              busy={control.isPending && control.variables?.index === ng.index} onToggle={(v) => control.mutate({ index: ng.index, on: v })} />
          ))}
        </div>
      )}

      {tab === 'den' && coNema && <TabDenNema cabinetId={c.id} tenTu={c.name} qd={qd} />}

      {tab === 'lich' && (
        <div className="overflow-x-auto" data-o="lich-van-hanh">
          <table className="w-full border-collapse text-[11px]">
            <thead><tr className="bg-nen-2 text-chu-2">
              <th className="px-1.5 py-[7px] text-left font-normal">Ngõ ra</th><th className="px-1.5 py-[7px] text-left font-normal">Chế độ</th>
              <th className="px-1.5 py-[7px] text-right font-normal">Bật kế tiếp</th><th className="px-1.5 py-[7px] text-right font-normal">Tắt kế tiếp</th>
              <th className="px-1.5 py-[7px] text-right font-normal">Timer bật</th>
            </tr></thead>
            <tbody>
              {c.outputs.map((ng) => (
                <tr key={ng.id} className="border-b border-vien text-chu-1">
                  <td className="px-1.5 py-[7px]">{ng.name || `Ngõ ra ${ng.index}`}</td>
                  <td className="px-1.5 py-[7px]">{MODE_LABEL[ng.mode]}</td>
                  <td className="px-1.5 py-[7px] text-right tabular-nums">{ng.nextOnMin != null ? minToStr(ng.nextOnMin) : '—'}</td>
                  <td className="px-1.5 py-[7px] text-right tabular-nums">{ng.nextOffMin != null ? minToStr(ng.nextOffMin) : '—'}</td>
                  <td className="px-1.5 py-[7px] text-right tabular-nums">{ng.timers.filter((t) => t.enabled).length}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Link to={`/lich/${c.id}`} className="mt-2 inline-block text-xs text-nhan hover:underline">Mở lịch đóng cắt</Link>
        </div>
      )}

      {tab === 'lich-su' && (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <HopVanDe pham={c.id} tieuDe="Vấn đề của tủ" compact />
          <section className="min-w-0">
            <h3 className="m-0 mb-2 text-xs font-semibold text-chu-1">Lệnh gần đây</h3>
            {commands.isLoading ? <Spinner label="Đang tải…" />
              : (commands.data ?? []).length === 0 ? <p className="m-0 text-xs text-chu-3">Chưa có lệnh nào</p>
                : <BangLenh ds={(commands.data ?? []).slice(0, 10)} kieuXacNhan={false} />}
          </section>
        </div>
      )}
    </section>
  );
}
